import { call, put, takeLatest, } from "redux-saga/effects";
import { authSagaActionTypes, getLoggedUserDetailSagaAction } from "./saga-actions";
import { authSliceAction } from "../../slices/auth";
import { axiosGraphqlInstance, axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import type { RolePermissionItem, UserDetailType } from "../../slices/user/Types";
import { getDecryptedItem, setEncryptedItem } from "../../../utils/localStorageUtils";
import {
    flattenRolePermissionsForPortal,
    getPermissionPortalKey,
} from "../../../utils/permissions/flattenRolePermissions";

type LoginDataResponseType = {
    data: {
        customerAccessTokenCreate: {
            customerAccessToken: { accessToken: string, expiresAt: string }
            customerUserErrors: []
        }
    }
}
const getAuthDetailAPI = async (userDetail: { email?: string, password?: string }) => {
    let response = await axiosGraphqlInstance.post<LoginDataResponseType | {}>('/graphql.json',
        {
            query: `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
        customerAccessTokenCreate(input : $input) {
          customerUserErrors {
            code
            field
            message
          }
          customerAccessToken {
            accessToken
            expiresAt
          }
        }
      }`,
            variables: {
                "input": {
                    "email": userDetail?.email,
                    "password": userDetail?.password
                }
            }
        }
    )
        .then((response) => {
            if (response.status === 200) {
                return response
            }
        })
        .catch((error) => {
            throw error
        })
    return response.data
}

type CutomerRecoverResponseType = {
    data: {
        customerRecover: {
            customerUserErrors: [] | [{
                message: string;
            }]
        }
    },
    errors: [] | [{
        message: string;
    }]
}
const customerRecoverAPI = async (email: string) => {
    const query = `
        mutation CustomerRecover($email : String!) {
            customerRecover(email:$email) {
                customerUserErrors {
                    code
                    field
                    message
                }
            }
        }
    `;
    let response = await axiosGraphqlInstance.post<CutomerRecoverResponseType | {}>('/graphql.json',
        {
            query,
            variables: {
                email
            }
        }
    )
        .then((response) => {
            if (response.status === 200) {
                return response
            }
        })
        .catch((error) => {
            throw error
        })
    return response.data
}

type CutomerResetAPIResponseType = {
    data: {
        customerReset: {
            customer: {
                email: string;
            };
            customerAccessToken: { 
                accessToken: string;
                expiresAt: string;
            };
            customerUserErrors: [] | [{
                message: string;
            }];
        }
    },
    errors: [] | [{
        message: string;
    }]
}
const customerResetAPI = async (variables: { id?: string, password?: string, resetToken?: string}) => {
    const query = `
        mutation customerReset($id: ID!, $input: CustomerResetInput!) {
            customerReset(id: $id, input: $input) {
                customer {
                    email
                }
                customerAccessToken {
                    accessToken
                    expiresAt
                }
                customerUserErrors {
                    code
                    field
                    message
                }
            }
        }
    `;
    let response = await axiosGraphqlInstance.post<CutomerResetAPIResponseType | {}>('/graphql.json',
        {
            query,
            variables: {
                id: `gid://shopify/Customer/${variables.id}`,
                input: {
                    password: variables.password,
                    resetToken: variables.resetToken
                }
            }
        }
    )
        .then((response) => {
            if (response.status === 200) {
                return response
            }
        })
        .catch((error) => {
            throw error
        })
    return response.data
}

type CustomerActivateAPIResponseType = {
    data: {
        customerActivate: {
            customer: {
                id: string;
                email: string;
            };
            customerAccessToken: { 
                accessToken: string;
                expiresAt: string;
            };
            customerUserErrors: [] | [{
                message: string;
            }];
        }
    },
    errors: [] | [{
        message: string;
    }]
}
const customerActivateAPI = async (variables: { id: string, activationToken: string, password: string }) => {
    const query = `
        mutation customerActivate($id: ID!, $input: CustomerActivateInput!) {
            customerActivate(id: $id, input: $input) {
                customer {
                    id
                    email
                }
                customerAccessToken {
                    accessToken
                    expiresAt
                }
                customerUserErrors {
                    message
                }
            }
        }
    `;
    let response = await axiosGraphqlInstance.post<CustomerActivateAPIResponseType | {}>('/graphql.json',
        {
            query,
            variables: {
                id: `gid://shopify/Customer/${variables.id}`,
                input: {
                    activationToken: variables.activationToken,
                    password: variables.password
                }
            }
        })
        .then((response) => {
            if (response.status === 200) {
                return response
            }
        })
        .catch((error) => {
            throw error
        })
    return response.data
}

type GetLoggedUserDetailAPIResponseType = {
    success: boolean;
    message: string;
    data: UserDetailType;
}
const getLoggedUserDetailAPI = async () => {
    let userEmail = getDecryptedItem("user-email")?.replace("+", "%2B");
    let response = axiosRestInstance.get<AxiosResponse<GetLoggedUserDetailAPIResponseType>>('/user/getUser?email=' + userEmail)
        .then((res) => res.data);
    return response;
  }

type PermissionGetApiResponse = {
    success: boolean;
    data?: Record<string, Record<string, { permission_key: string; action: string }[]>>;
};

const getRolePermissionsForSessionAPI = async (roleId: number) => {
    const response = await axiosRestInstance
        .get<AxiosResponse<PermissionGetApiResponse>>(`/permission/get/${roleId}`)
        .then((res) => res.data);
    return response;
};

type RoleTypeRow = { type?: string };

const getRoleTypeByIdAPI = async (roleId: number) => {
    const response = await axiosRestInstance
        .get<AxiosResponse<RoleTypeRow[]>>(`/user/getRole?ID=${roleId}`)
        .then((res) => res.data);
    return response;
};

//-----API Above-----

function* getLogin(actions: {
    type: string,
    payload: { email: string, password: string }
}) {
    try {;
        const response: LoginDataResponseType = yield call(getAuthDetailAPI, actions.payload);
        if (response.data.customerAccessTokenCreate.customerUserErrors.length === 0) {
            yield put(authSliceAction.setLoginDetails(response.data));
            localStorage.setItem("user-token", response.data.customerAccessTokenCreate.customerAccessToken.accessToken);
            localStorage.setItem("user-token-expiry-date", response.data.customerAccessTokenCreate.customerAccessToken.expiresAt);
            setEncryptedItem("user-email", actions?.payload?.email);
            yield put(authSliceAction.errorInfoTrigger({
                severity: "",
                errorMsg: "",
                open: false
            }))
        } else {
            yield put(authSliceAction.errorInfoTrigger({
                severity: "error",
                errorMsg: "Invalid E-Mail or Password",
                open: true
            }))
        }
    } catch (e) {
        console.error('====>error from axios', e)
        yield put(authSliceAction.errorInfoTrigger({
            severity: "error",
            errorMsg: "Technical Error!",
            open: true
        }))
    }
}

function* customerRecoverSaga(actions: {
    type: string,
    payload: { email: string }
}) {
    try {;
        const response: CutomerRecoverResponseType = yield call(customerRecoverAPI, actions.payload.email);
        if (response.data.customerRecover?.customerUserErrors.length === 0) {
            const msg = `The password reset email has been sent to ${actions.payload.email}`;
            yield put(authSliceAction.setCustRecoverSuccessMsg(msg));
            yield put(authSliceAction.errorInfoTrigger({
                severity: "",
                errorMsg: "",
                open: true
            }))
        } else {
            const msg = response.data.customerRecover ? 
                response.data.customerRecover.customerUserErrors[0].message :
                response.errors[0].message;
                
            yield put(authSliceAction.errorInfoTrigger({
                severity: "error",
                errorMsg: msg,
                open: true
            }))
        }
    } catch (e) {
        console.error('====>error from axios', e)
        yield put(authSliceAction.errorInfoTrigger({
            severity: "error",
            errorMsg: "Technical Error!",
            open: true
        }))
    }
}

function* customerResetSaga(actions: {
    type: string,
    payload: { id: string, password: string, resetToken: string }
}) {
    try {
        const response: CutomerResetAPIResponseType = yield call(customerResetAPI, actions.payload);
        if (response.data.customerReset?.customerUserErrors.length === 0) {
            yield put(authSliceAction.setLoginDetails(response.data));
            localStorage.setItem("user-token", response.data.customerReset.customerAccessToken.accessToken);
            localStorage.setItem("user-token-expiry-date", response.data.customerReset.customerAccessToken.expiresAt);
            setEncryptedItem("user-email", response.data.customerReset.customer.email);
        } else {
            const msg = response.data.customerReset ? 
                response.data.customerReset.customerUserErrors[0].message :
                response.errors[0].message;
            yield put(authSliceAction.errorInfoTrigger({
                severity: "error",
                errorMsg: msg,
                open: true
            }))
        }
    } catch (e) {
        console.error('====>error from axios', e)
        yield put(authSliceAction.errorInfoTrigger({
            severity: "error",
            errorMsg: "Technical Error!",
            open: true
        }))
    }
}

function* customerActivateSaga(actions: {
    type: string,
    payload: { id: string, activationToken: string, password: string }
}) {
    try {
        const response: CustomerActivateAPIResponseType = yield call(customerActivateAPI, actions.payload);
        if (response.data.customerActivate?.customerUserErrors.length === 0) {
            yield put(authSliceAction.setLoginDetails(response.data));
            localStorage.setItem("user-token", response.data.customerActivate.customerAccessToken.accessToken);
            localStorage.setItem("user-token-expiry-date", response.data.customerActivate.customerAccessToken.expiresAt);
            setEncryptedItem("user-email", response.data.customerActivate.customer.email);
        } else {
            const msg = response.data.customerActivate ?
                response.data.customerActivate.customerUserErrors[0].message :
                response.errors[0].message;
            yield put(authSliceAction.errorInfoTrigger({
                severity: "error",
                errorMsg: msg,
                open: true
            }))
        }
    } catch (e) {
        console.error('====>error from axios', e)
        yield put(authSliceAction.errorInfoTrigger({
            severity: "error",
            errorMsg: "Technical Error!",
            open: true
        }))
    }
}

function* getLoggedUserDetailSaga(actions: {
    type: string,
}) {
    try {
        yield put(authSliceAction.setLoggedUserDetailLoading(true));
        const response: GetLoggedUserDetailAPIResponseType = yield call(getLoggedUserDetailAPI);
        let userData = response.data;

        if (userData.role_id && !userData.role_type) {
            try {
                const roleRows: RoleTypeRow[] = yield call(
                    getRoleTypeByIdAPI,
                    userData.role_id
                );
                const roleType = Array.isArray(roleRows) ? roleRows[0]?.type : undefined;
                if (roleType) {
                    userData = { ...userData, role_type: roleType };
                }
            } catch (roleErr) {
                console.error("getRole type fallback failed", roleErr);
            }
        }

        yield put(authSliceAction.setLoggedUserDetail(userData));

        const roleId = userData.role_id;
        if (!roleId) {
            yield put(authSliceAction.setSessionPermissionMap({}));
            yield put(authSliceAction.setSessionPartnerPermissionMap({}));
            yield put(authSliceAction.setSessionPermissionsLoading(false));
            yield put(authSliceAction.setSessionPermissionsLoaded(true));
        } else {
            yield put(authSliceAction.setSessionPermissionsLoading(true));
            yield put(authSliceAction.setSessionPermissionsLoaded(false));
            try {
                const permResponse: PermissionGetApiResponse = yield call(
                    getRolePermissionsForSessionAPI,
                    roleId
                );
                if (permResponse.success && permResponse.data) {
                    const permData = permResponse.data as Record<
                        string,
                        Record<string, RolePermissionItem[]>
                    >;
                    const portal = getPermissionPortalKey(
                        userData.role,
                        userData.role_type
                    );
                    const map = flattenRolePermissionsForPortal(permData, portal);
                    const partnerMap = flattenRolePermissionsForPortal(
                        permData,
                        "partner"
                    );
                    yield put(authSliceAction.setSessionPermissionMap(map));
                    yield put(authSliceAction.setSessionPartnerPermissionMap(partnerMap));
                } else {
                    yield put(authSliceAction.setSessionPermissionMap({}));
                    yield put(authSliceAction.setSessionPartnerPermissionMap({}));
                }
            } catch (permErr) {
                console.error("permission/get failed", permErr);
                yield put(authSliceAction.setSessionPermissionMap({}));
                yield put(authSliceAction.setSessionPartnerPermissionMap({}));
            } finally {
                yield put(authSliceAction.setSessionPermissionsLoading(false));
                yield put(authSliceAction.setSessionPermissionsLoaded(true));
            }
        }
    } catch (e) {
        console.error('====>error from axios', e)
        yield put(authSliceAction.setSessionPermissionMap({}));
        yield put(authSliceAction.setSessionPartnerPermissionMap({}));
        yield put(authSliceAction.setSessionPermissionsLoading(false));
        yield put(authSliceAction.setSessionPermissionsLoaded(false));
        yield put(authSliceAction.errorInfoTrigger({
            severity: "error",
            errorMsg: "Technical Error!",
            open: true
        }))
    } finally {
        yield put(authSliceAction.setLoggedUserDetailLoading(false));
    }
}

function* authSaga() {
    yield takeLatest(authSagaActionTypes.callAuthSaga, getLogin);
    yield takeLatest(authSagaActionTypes.callCustomerRecoverSaga, customerRecoverSaga);
    yield takeLatest(authSagaActionTypes.callCustomerResetSaga, customerResetSaga);
    yield takeLatest(authSagaActionTypes.callCustomerActivateSaga, customerActivateSaga);
    yield takeLatest(authSagaActionTypes.callGetLoggedUserDetailSaga, getLoggedUserDetailSaga);
}

export default authSaga;